/**
 * useActivityHeatmap Hook
 * Fetches and manages activity heatmap data
 */

import { useState, useEffect, useCallback } from 'react'
import activityService from '../../services/activityService'
import { createLogger } from '../../utils/logger'

const logger = createLogger('useActivityHeatmap')

/**
 * Hook to fetch and manage activity heatmap data
 * @param {Object} options - Hook options
 * @param {boolean} options.autoFetch - Whether to fetch data automatically (default: true)
 * @param {string} options.userId - Optional user ID to fetch activity for (for coach/guardian views)
 * @returns {Object} Activity heatmap data and loading state
 */
export function useActivityHeatmap({ autoFetch = true, userId = null } = {}) {
  const [activities, setActivities] = useState({})
  const [stats, setStats] = useState({
    totalActiveDays: 0,
    currentStreak: 0,
    longestStreak: 0,
    maxDailyActivity: 0
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await activityService.getActivityHeatmap(userId)

      if (response.success) {
        setActivities(response.data.activities || {})
        setStats(
          response.data.stats || {
            totalActiveDays: 0,
            currentStreak: 0,
            longestStreak: 0,
            maxDailyActivity: 0
          }
        )
      } else {
        throw new Error(response.error || 'Failed to fetch activity data')
      }
    } catch (err) {
      logger.error('Error fetching activity heatmap:', err)
      setError(err.message || 'Failed to load activity data')
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    if (autoFetch) {
      fetchData()
    }
  }, [autoFetch, fetchData])

  return {
    activities,
    stats,
    loading,
    error,
    refetch: fetchData
  }
}

export default useActivityHeatmap
